import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-2",
  "",
  "",
  "md:col-span-2",
  "md:col-span-2",
];

export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cases");
        setCases(data);
      } catch {
        setCases([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const jsonLd = useMemo(() => ([
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Selected Work — Spin Labs",
      url: "https://spinlabs.in/work",
      description: "Real products, real numbers. Selected case studies from Spin Labs across mobile, AI, SaaS and software.",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: cases.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://spinlabs.in/work/${c.slug}`,
        name: c.title,
      })),
    },
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Work", path: "/work" },
    ]),
  ]), [cases]);

  return (
    <div data-testid="case-studies-page">
      <Seo
        title="Selected Work — Case Studies"
        path="/work"
        description="A slice of what Spin Labs has shipped — mobile apps, AI systems, SaaS platforms and software for fast-moving teams."
        keywords="case studies, software portfolio, AI projects, mobile app case studies, SaaS case studies, Spin Labs work"
        jsonLd={jsonLd}
      />
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[600px] h-[600px] bg-[#00F0FF]/15 top-0 right-0" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6">Selected Work</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.9] text-balance max-w-5xl">
              Real products. <span className="gradient-text">Real numbers.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
              A slice of what we've shipped in the last 18 months. Click any to read the full story.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="h-96 flex items-center justify-center text-white/40">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 auto-rows-[260px]">
              {cases.map((c, i) => (
                <motion.article
                  key={c.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  data-testid={`case-card-${c.slug}`}
                  className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#00F0FF]/40 transition-all ${SPANS[i] || ""}`}
                >
                  <Link to={`/work/${c.slug}`} className="block h-full">
                    <img
                      src={c.cover_img}
                      alt={c.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                    <div className="relative h-full flex flex-col justify-between p-6 lg:p-8">
                      <div>
                        <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em]" style={{ color: c.color }}>{c.category}</div>
                        <h3 className="mt-3 font-display text-2xl lg:text-3xl font-black tracking-tighter">{c.title}</h3>
                      </div>
                      <div>
                        <p className="text-sm text-white/70 leading-relaxed mb-4 max-w-md">{c.tagline}</p>
                        <div className="flex items-center justify-between">
                          <div className="font-mono-neo text-sm font-bold" style={{ color: c.color }}>{c.metric}</div>
                          <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-[#00F0FF] group-hover:text-black transition-all">
                            <ArrowUpRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}

          <div className="mt-16 text-center">
            <Link to="/contact" className="btn-primary !px-8 !py-4 !text-base group">
              Want your story here?
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
